import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Tag, Plus, Pencil, Trash2, X, Save, Calendar, Percent, DollarSign, FolderOpen } from 'lucide-react'
import { promotionsService } from '@/services/promotionsService'
import { categoriesService } from '@/services/categoriesService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatDate } from '@/utils/formatters'

function DialogOverlay({ children, onClose }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)' }} onClick={onClose}>
      <div className="animate-in" style={{ background:'#fff',borderRadius:24,padding:28,width:'100%',maxWidth:520,boxShadow:'0 32px 64px rgba(0,0,0,0.2)',position:'relative',maxHeight:'92vh',overflowY:'auto' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position:'absolute',top:16,left:16,width:32,height:32,borderRadius:10,background:'#F3F3F3',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#767683' }}><X size={16}/></button>
        {children}
      </div>
    </div>
  )
}

const TYPES = [
  { value:'percentage', label:'نسبة مئوية', icon: Percent },
  { value:'fixed',      label:'مبلغ ثابت',   icon: DollarSign },
]

const EMPTY = { nameAr:'', nameEn:'', couponCode:'', discountType:'percentage', discountValue:'', minimumOrderAmount:'', maxUsageCount:'', startDate:'', endDate:'', isActive:true, categoryIds:[] }

function CategoryPicker({ selected, onChange }) {
  const { data: tree } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
  })

  const flatCats = []
  const flatten = (arr, depth = 0) => {
    arr?.forEach((c) => {
      flatCats.push({ id: c.id, name: c.name?.ar || c.name?.en || '', depth })
      if (c.children?.length) flatten(c.children, depth + 1)
    })
  }
  flatten(tree)

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])
  }

  return (
    <div style={{ maxHeight:180, overflowY:'auto', border:'1px solid #EBEBEB', borderRadius:10, padding:6, display:'flex', flexDirection:'column', gap:2 }}>
      {flatCats.length === 0 ? (
        <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683', textAlign:'center', padding:12, margin:0 }}>لا توجد فئات</p>
      ) : (
        flatCats.map((cat) => (
          <label key={cat.id}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 8px', borderRadius:8, cursor:'pointer', background: selected.includes(cat.id) ? 'rgba(0,6,102,0.06)' : 'transparent' }}>
            <input type="checkbox" checked={selected.includes(cat.id)} onChange={() => toggle(cat.id)}
              style={{ width:15, height:15, accentColor:'#000666', cursor:'pointer', flexShrink:0 }} />
            <span style={{ fontFamily:'Cairo, sans-serif', fontSize:12, fontWeight: selected.includes(cat.id) ? 700 : 500, color:'#1A1C1C', marginRight: cat.depth * 14 }}>
              {cat.depth > 0 && '— '.repeat(cat.depth)}{cat.name}
            </span>
          </label>
        ))
      )}
    </div>
  )
}

export default function PromotionsPage() {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()

  const { page, limit, setPage } = usePagination(1, 12)
  const [formDialog, setFormDialog] = useState({ open:false, mode:'create', promo:null })
  const [delDialog,  setDelDialog]  = useState({ open:false, promo:null })
  const [form, setForm] = useState(EMPTY)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['promotions-admin', { page, limit }],
    queryFn: () => promotionsService.getAll({ page, limit }),
  })

  const promos = data?.data || []
  const meta   = data?.meta || {}

  const inv = () => qc.invalidateQueries({ queryKey:['promotions-admin'] })

  const createMut = useMutation({ mutationFn: (d) => promotionsService.create(d),           onSuccess: () => { toast.success('تم إنشاء العرض'); inv(); closeForm() }, onError: (e) => toast.error(e?.response?.data?.message || 'فشل الإنشاء') })
  const updateMut = useMutation({ mutationFn: ({ id,d }) => promotionsService.update(id,d), onSuccess: () => { toast.success('تم التحديث');   inv(); closeForm() }, onError: (e) => toast.error(e?.response?.data?.message || 'فشل التحديث') })
  const deleteMut = useMutation({ mutationFn: (id) => promotionsService.delete(id),         onSuccess: () => { toast.success('تم الحذف');     inv(); setDelDialog({ open:false,promo:null }) }, onError: () => toast.error('فشل الحذف') })

  const openEdit = (promo) => {
    setForm({
      nameAr: promo.name?.ar||'', nameEn: promo.name?.en||'',
      couponCode: promo.code||'',
      discountType: promo.discountType||'percentage',
      discountValue: promo.discountValue||'',
      minimumOrderAmount: promo.minPurchaseAmount && Number(promo.minPurchaseAmount) > 0 ? promo.minPurchaseAmount : '',
      maxUsageCount: promo.usageLimitTotal||'',
      startDate: promo.startsAt?.slice(0,16)||'',
      endDate: promo.endsAt?.slice(0,16)||'',
      isActive: promo.isActive,
      categoryIds: (promo.categories||[]).map((c) => c.id),
    })
    setFormDialog({ open:true, mode:'edit', promo })
  }

  const closeForm = () => { setFormDialog({ open:false,mode:'create',promo:null }); setForm(EMPTY) }

  const toISO = (v) => (v ? new Date(v).toISOString() : undefined)

  const handleSubmit = () => {
    const base = {
      name: { ar: form.nameAr, en: form.nameEn || form.nameAr },
      discountValue: parseFloat(form.discountValue) || 0,
      minPurchaseAmount: form.minimumOrderAmount ? parseFloat(form.minimumOrderAmount) : 0,
      usageLimitTotal: form.maxUsageCount ? parseInt(form.maxUsageCount) : undefined,
      startsAt: toISO(form.startDate) || new Date().toISOString(),
      endsAt: toISO(form.endDate),
      isActive: form.isActive,
      categoryIds: form.categoryIds,
    }
    if (formDialog.mode==='create') {
      createMut.mutate({
        ...base,
        type: 'coupon',
        code: form.couponCode.toUpperCase(),
        discountType: form.discountType,
      })
    } else {
      // type / code / discountType are immutable after creation
      const d = { ...base }
      if (formDialog.promo.usageCount > 0) delete d.discountValue
      updateMut.mutate({ id: formDialog.promo.id, d })
    }
  }

  const isPending = createMut.isPending || updateMut.isPending
  const LBL = { display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }
  const INP = { width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Tajawal, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }

  const TYPE_ICONS = { percentage: Percent, fixed: DollarSign }
  const TYPE_COLOR = { percentage:'rgba(0,6,102,0.08)', fixed:'rgba(27,109,36,0.08)' }
  const TYPE_TEXT  = { percentage:'#000666', fixed:'#1B6D24' }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => { setForm(EMPTY); setFormDialog({ open:true,mode:'create',promo:null }) }}
          style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.2)' }}>
          <Plus size={16}/>إضافة عرض
        </button>
      </div>

      {isLoading ? <div className="flex justify-center py-20"><LoadingSpinner/></div>
      : isError ? <div className="hg-card p-8 flex flex-col items-center gap-4"><p style={{ color:'#BA1A1A',fontWeight:700,fontFamily:'Cairo, sans-serif' }}>فشل التحميل</p><button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button></div>
      : promos.length === 0 ? <div className="hg-card"><EmptyState icon={Tag} message="لا توجد عروض ترويجية بعد"/></div>
      : (
        <>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))',gap:16 }}>
            {promos.map((promo) => {
              const TIcon = TYPE_ICONS[promo.discountType] || Percent
              const bg    = TYPE_COLOR[promo.discountType]
              const clr   = TYPE_TEXT[promo.discountType]
              const name  = promo.name?.[lang] || promo.name?.ar || promo.name?.en || '—'
              return (
                <div key={promo.id} style={{ background:'#fff',borderRadius:20,padding:20,boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)',transition:'transform 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.transform='translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform='translateY(0)'}
                >
                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:12 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                      <div style={{ width:42,height:42,borderRadius:12,background:bg,color:clr,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}><TIcon size={20}/></div>
                      <div>
                        <div style={{ fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#1A1C1C' }}>{name}</div>
                        <code style={{ fontSize:13,fontWeight:700,color:clr,background:bg,padding:'2px 8px',borderRadius:6 }}>{promo.code}</code>
                      </div>
                    </div>
                    <span style={{ fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:8,fontFamily:'Cairo, sans-serif', background: promo.isActive?'rgba(27,109,36,0.08)':'rgba(186,26,26,0.08)', color: promo.isActive?'#1B6D24':'#BA1A1A' }}>
                      {promo.isActive?'نشط':'متوقف'}
                    </span>
                  </div>

                  <div style={{ display:'flex',gap:12,flexWrap:'wrap',marginBottom:12 }}>
                    <span style={{ fontSize:13,fontWeight:700,fontFamily:'Cairo, sans-serif',color:clr }}>
                      {promo.discountType==='percentage' ? `${promo.discountValue}%` : `${promo.discountValue} ج.م`}
                    </span>
                    {Number(promo.minPurchaseAmount) > 0 && <span style={{ fontSize:12,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>حد أدنى: {promo.minPurchaseAmount} ج.م</span>}
                    {promo.usageLimitTotal && <span style={{ fontSize:12,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>حد الاستخدام: {promo.usageLimitTotal}</span>}
                  </div>

                  {(promo.categories?.length > 0) && (
                    <div style={{ display:'flex',gap:4,flexWrap:'wrap',marginBottom:12,alignItems:'center' }}>
                      <FolderOpen size={11} style={{ color:'#767683' }}/>
                      {promo.categories.slice(0,4).map((cat) => (
                        <span key={cat.id} style={{ fontSize:10,padding:'2px 8px',borderRadius:6,background:'rgba(0,6,102,0.06)',color:'#000666',fontFamily:'Tajawal, sans-serif' }}>
                          {cat.name?.[lang] || cat.name?.ar || cat.name?.en}
                        </span>
                      ))}
                      {promo.categories.length > 4 && <span style={{ fontSize:10,color:'#767683' }}>+{promo.categories.length - 4}</span>}
                    </div>
                  )}

                  {(promo.startsAt || promo.endsAt) && (
                    <div style={{ fontSize:11,color:'#767683',fontFamily:'Tajawal, sans-serif',marginBottom:12,display:'flex',alignItems:'center',gap:5 }}>
                      <Calendar size={11}/>
                      {promo.startsAt && formatDate(promo.startsAt, lang==='ar'?'ar-EG':'en')} {promo.endsAt && `— ${formatDate(promo.endsAt, lang==='ar'?'ar-EG':'en')}`}
                    </div>
                  )}

                  <div style={{ display:'flex',gap:8,borderTop:'1px solid #F3F3F3',paddingTop:12 }}>
                    <button onClick={() => openEdit(promo)} style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'8px 0',borderRadius:10,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',background:'rgba(0,6,102,0.06)',color:'#000666',border:'none',cursor:'pointer' }}>
                      <Pencil size={13}/>تعديل
                    </button>
                    <button onClick={() => setDelDialog({ open:true,promo })} style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'8px 0',borderRadius:10,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer' }}>
                      <Trash2 size={13}/>حذف
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="hg-card p-4"><Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage}/></div>
        </>
      )}

      {/* Form dialog */}
      {formDialog.open && (
        <DialogOverlay onClose={closeForm}>
          <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:20 }}>{formDialog.mode==='create'?'إضافة عرض ترويجي':'تعديل العرض'}</h3>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14 }}>
            <div><label style={LBL}>الاسم بالعربي</label><input value={form.nameAr} onChange={(e) => setForm((p) => ({ ...p,nameAr:e.target.value }))} style={INP} placeholder="عرض الصيف"/></div>
            <div><label style={LBL}>الاسم بالإنجليزي</label><input value={form.nameEn} onChange={(e) => setForm((p) => ({ ...p,nameEn:e.target.value }))} dir="ltr" style={INP} placeholder="Summer Deal"/></div>
          </div>
          <div style={{ marginBottom:14 }}><label style={LBL}>كود الكوبون</label><input value={form.couponCode} disabled={formDialog.mode==='edit'} onChange={(e) => setForm((p) => ({ ...p,couponCode:e.target.value.toUpperCase() }))} dir="ltr" style={{ ...INP,fontWeight:700,letterSpacing:'0.1em',opacity: formDialog.mode==='edit'?0.6:1 }} placeholder="SUMMER20"/></div>
          <div style={{ marginBottom:14 }}>
            <label style={LBL}>نوع الخصم</label>
            <div style={{ display:'flex',gap:8 }}>
              {TYPES.map(({ value,label,icon:Icon }) => (
                <button key={value} disabled={formDialog.mode==='edit'} onClick={() => setForm((p) => ({ ...p,discountType:value }))}
                  style={{ flex:1,padding:'10px 8px',borderRadius:10,border:'none',cursor:'pointer',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,display:'flex',flexDirection:'column',alignItems:'center',gap:4,transition:'all 0.2s',
                    background: form.discountType===value ? '#000666' : '#F3F3F3',
                    color: form.discountType===value ? '#fff' : '#454652' }}>
                  <Icon size={16}/>{label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14 }}>
            <div><label style={LBL}>قيمة الخصم</label><input type="number" min="0" step="0.1" value={form.discountValue} onChange={(e) => setForm((p) => ({ ...p,discountValue:e.target.value }))} style={INP}/></div>
            <div><label style={LBL}>الحد الأدنى للطلب</label><input type="number" min="0" value={form.minimumOrderAmount} onChange={(e) => setForm((p) => ({ ...p,minimumOrderAmount:e.target.value }))} style={INP} placeholder="اختياري"/></div>
            <div><label style={LBL}>حد الاستخدام الكلي</label><input type="number" min="0" value={form.maxUsageCount} onChange={(e) => setForm((p) => ({ ...p,maxUsageCount:e.target.value }))} style={INP} placeholder="اختياري"/></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14 }}>
            <div><label style={LBL}>تاريخ البداية</label><input type="datetime-local" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p,startDate:e.target.value }))} style={{ ...INP,cursor:'pointer' }}/></div>
            <div><label style={LBL}>تاريخ الانتهاء</label><input type="datetime-local" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p,endDate:e.target.value }))} style={{ ...INP,cursor:'pointer' }}/></div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={LBL}>الفئات المشمولة بالكوبون</label>
            <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:11,color:'#767683',margin:'0 0 6px' }}>اترك الكل بدون تحديد ليشمل الكوبون جميع المنتجات</p>
            <CategoryPicker selected={form.categoryIds} onChange={(ids) => setForm((p) => ({ ...p,categoryIds:ids }))}/>
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:18 }}>
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p,isActive:e.target.checked }))} id="pa"/>
            <label htmlFor="pa" style={{ fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652',cursor:'pointer' }}>نشط</label>
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={handleSubmit} disabled={isPending} style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:isPending?0.6:1 }}>
              <Save size={15}/>{isPending?'...':'حفظ'}
            </button>
            <button onClick={closeForm} style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,background:'#F3F3F3',color:'#454652',border:'none',cursor:'pointer' }}>إلغاء</button>
          </div>
        </DialogOverlay>
      )}

      <ConfirmDialog open={delDialog.open} title="حذف العرض" message={`حذف عرض "${delDialog.promo?.name?.ar || delDialog.promo?.code}"؟`} variant="danger" loading={deleteMut.isPending} onConfirm={() => deleteMut.mutate(delDialog.promo.id)} onCancel={() => setDelDialog({ open:false,promo:null })}/>
    </div>
  )
}
